<template>
    <div class="container-fluid">
        <div v-if="isRoutePage" class="mb-3">
            <BreadcrumbNav />
        </div>
        <!-- Tab Legend -->
        <div class="d-flex align-items-center gap-3 mb-2 text-xs text-secondary">
            <span><i class="material-icons text-danger" style="font-size: 12px; vertical-align: middle;">star</i> Required</span>
            <span><i class="material-icons text-muted" style="font-size: 12px; vertical-align: middle;">radio_button_unchecked</i> Optional</span>
        </div>

        <NavTabs>
            <NavTab target="skill-details" :activeTab="activeTab" @update:activeTab="activeTab = $event">
                <span title="Skill Details (Required)">
                    Details <i class="material-icons text-danger" style="font-size: 12px; vertical-align: middle;">star</i>
                </span>
            </NavTab>
            <NavTab target="add-remove" :activeTab="activeTab" @update:activeTab="activeTab = $event">
                <span title="Add/Remove Persons (Required)">
                    Persons <i class="material-icons text-danger" style="font-size: 12px; vertical-align: middle;">star</i>
                </span>
            </NavTab>
            <NavTab target="update" :activeTab="activeTab" @update:activeTab="activeTab = $event">
                <span title="Update Persons Skill Level (Optional)">
                    Skill Levels <i class="material-icons text-muted" style="font-size: 12px; vertical-align: middle;">radio_button_unchecked</i>
                </span>
            </NavTab>
            <NavTab target="workgroups" :activeTab="activeTab" @update:activeTab="activeTab = $event">
                <span title="Workgroups that require this skill (Optional)">
                    Workgroups <i class="material-icons text-muted" style="font-size: 12px; vertical-align: middle;">radio_button_unchecked</i>
                </span>
            </NavTab>
            <NavTab target="interested-not-assigned" :activeTab="activeTab" @update:activeTab="activeTab = $event">
                <span title="Users that are interested in this skill (but not yet assigned) (Optional)">
                    Interested Users <i class="material-icons text-muted" style="font-size: 12px; vertical-align: middle;">radio_button_unchecked</i>
                </span>
            </NavTab>
        </NavTabs>

        <ContentContainer>
            <template #content>
                <ExplainPageButton :page-key="`skill.${activeTab}`" />
                <div v-if="activeTab === 'skill-details'">
                    <p class="text-sm text-secondary mb-3 px-3 pt-3">View and edit the core settings for this skill, including name and visibility options.</p>
                    <SkillDetails :skillId="skillId" :locationId="locationId" :readOnly="props.viewOnly" />
                </div>

                <div v-else-if="activeTab === 'add-remove'">
                    <p class="text-sm text-secondary mb-3 px-3 pt-3">Add or remove persons who have this skill.</p>
                    <AddRemove :skillId="skillId" :locationId="locationId" :readOnly="personsReadOnly" />
                </div>

                <div v-else-if="activeTab === 'update'">
                    <p class="text-sm text-secondary mb-3 px-3 pt-3">Update the skill level for persons assigned to this skill.</p>
                    <Update :skillId="skillId" :locationId="locationId" :readOnly="personsReadOnly" />
                </div>

                <div v-else-if="activeTab === 'workgroups'">
                    <p class="text-sm text-secondary mb-3 px-3 pt-3">View workgroups that require this skill.</p>
                    <Workgroups :skillId="skillId" :locationId="locationId" :readOnly="workgroupsReadOnly" />
                </div>

                <div v-else-if="activeTab === 'interested-not-assigned'">
                    <p class="text-sm text-secondary mb-3 px-3 pt-3">View users who are interested in this skill but have not yet been assigned.</p>
                    <InterestedNotAssigned :skillId="skillId" :locationId="locationId" :readOnly="interestedReadOnly" />
                </div>
            </template>
        </ContentContainer>
    </div>
</template>

<script setup>
import { ref, computed, watch } from "vue"
import { useRoute } from "vue-router"
import NavTab from "../../components/Tabs/NavTab.vue"
import NavTabs from "../../components/Tabs/NavTabs.vue"
import ContentContainer from "../../components/PageLayout/ContentContainer.vue"
import ExplainPageButton from "@/components/common/ExplainPageButton.vue"

import AddRemove from "./components/AddRemove.vue"
import InterestedNotAssigned from "./components/InterestedNotAssigned.vue"
import SkillDetails from "./components/SkillDetails.vue"
import Update from "./components/SkillLevels.vue"
import Workgroups from "./components/Workgroups.vue"
import BreadcrumbNav from "../../components/common/BreadcrumbNav.vue"
import { usePermissions } from "@/composables/usePermissions"
import { useBreadcrumb } from "@/composables/useBreadcrumb"

const props = defineProps({
    skillId: {
        type: [Number, String],
        default: null,
    },
    locationId: {
        type: [Number, String],
        default: null,
    },
    viewOnly: {
        type: Boolean,
        default: false,
    },
});

const route = useRoute();

// Use props if provided, otherwise fallback to route params
const skillId = computed(() => {
    return props.skillId !== null ? Number(props.skillId) : Number(route.params.skillid);
});

const locationId = computed(() => {
    return props.locationId !== null ? Number(props.locationId) : Number(route.params.locationId);
});

// Show GoBackButton only when accessed as a route page (not embedded in a modal)
const isRoutePage = computed(() => props.skillId === null);

const activeTab = ref("skill-details");

const { trail, setTrail } = useBreadcrumb();
const { canModifySkillPersons, isActivityAdmin } = usePermissions()

// Fallback trail for direct-URL access (route-based only)
if (props.skillId === null && !trail.value.length) {
    setTrail([
        { label: 'Skills', icon: 'workspace_premium', route: { name: 'skills', params: { locationid: route.params.locationId } } },
        { label: `Skill #${route.params.skillid}`, icon: 'workspace_premium' },
    ]);
}
const personsReadOnly = computed(() => !canModifySkillPersons(skillId.value).canEdit)
const workgroupsReadOnly = computed(() => !isActivityAdmin.value)
const interestedReadOnly = computed(() => !isActivityAdmin.value)

// Reset to first tab when skillId changes
watch(() => skillId.value, () => {
    activeTab.value = "skill-details";
}, { immediate: true });
</script>
